import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { GlobalBottomNavMobile, H3, ModalInMobile, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getActiveListingTypes,
  getSearchPageDefaultFiltersConfig,
  getSearchPageResourceLocatorStringParams,
  getActiveFiltersFromQuery,
  isSearchDirty,
  getTotalItemsFromPagination,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import {
  buildCanonicalSearchQueryKey,
  createSearchQueryKey,
  loadMoreListings,
} from './SearchPage.duck';

import css from './SearchPage.module.css';
import {
  clearSearchScrollState,
  getSearchRouteKey,
  readSearchScrollState,
  saveSearchScrollState,
} from '../../util/searchScrollState';
import { debugInfiniteScroll } from '../../util/infiniteScrollDebug';
import * as listingVisibility from '../../util/listingVisibility';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const RESTORE_SCROLL_MAX_TRIES = 120;
const { filterPublicListings } = listingVisibility;
const getDefaultFiltersConfig = (config, listingTypePathParam) => {
  const { defaultFilters: defaultFiltersRaw = [] } = config?.search || {};
  return getSearchPageDefaultFiltersConfig(defaultFiltersRaw, listingTypePathParam);
};

const getSelectedSecondaryFiltersCount = (
  validQueryParams,
  filterConfigs,
  customSecondaryFilters
) => {
  const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);
  const potentialSecondaryFilters = hasSecondaryFilters
    ? validFilterParams(validQueryParams, {
        ...filterConfigs,
        listingFieldsConfig: customSecondaryFilters,
      })
    : {};

  const relevantQueryParamNames = customSecondaryFilters.map(f => {
    return f.scope === 'public' ? `pub_${f.key}` : f.key;
  });
  const pickRelevant = name => relevantQueryParamNames.includes(name);
  const selectedSecondaryFilters = Object.keys(potentialSecondaryFilters).filter(pickRelevant);
  return selectedSecondaryFilters?.length;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      isMobileLayout: false,
    };

    this.handleViewportResize = this.handleViewportResize.bind(this);

    this.pendingScrollRestore = null;
    this.restoreScrollAttempts = 0;
    this.restoreScrollRaf = null;

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.handleKeywordSearch = this.handleKeywordSearch.bind(this);
    this.clearKeywordsOnly = this.clearKeywordsOnly.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.saveScrollStateBeforeListingNavigation = this.saveScrollStateBeforeListingNavigation.bind(
      this
    );
    this.tryRestoreSavedScrollPosition = this.tryRestoreSavedScrollPosition.bind(this);
    this.scheduleScrollRestore = this.scheduleScrollRestore.bind(this);
  }

  handleViewportResize() {
    this.setState({ isMobileLayout: window.innerWidth < MODAL_BREAKPOINT });
  }

  componentDidMount() {
    this.setState({ isMobileLayout: window.innerWidth < MODAL_BREAKPOINT });
    window.addEventListener('resize', this.handleViewportResize);

    this.pendingScrollRestore = readSearchScrollState(this.props.location);
    if (this.pendingScrollRestore) {
      this.restoreScrollAttempts = 0;
      this.scheduleScrollRestore();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      const urlQueryParams = validUrlQueryParamsFromProps(this.props);
      const currentQueryKey = createSearchQueryKey(this.state.currentQueryParams);
      const urlQueryKey = createSearchQueryKey(urlQueryParams);

      if (currentQueryKey !== urlQueryKey) {
        this.setState({ currentQueryParams: urlQueryParams });
      }
    }

    const listingsCountChanged = prevProps.listings?.length !== this.props.listings?.length;
    const searchProgressChanged = prevProps.searchInProgress !== this.props.searchInProgress;
    const locationChanged =
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.location.search !== this.props.location.search;

    if (
      this.pendingScrollRestore &&
      (listingsCountChanged || searchProgressChanged || locationChanged)
    ) {
      this.scheduleScrollRestore();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleViewportResize);
    if (this.restoreScrollRaf) {
      window.cancelAnimationFrame(this.restoreScrollRaf);
      this.restoreScrollRaf = null;
    }
  }

  getSearchQueryKey(validQueryParams) {
    const { params: currentPathParams = {} } = this.props;
    const { listingType: listingTypePathParam } = currentPathParams;
    const { config } = this.props;

    return buildCanonicalSearchQueryKey({
      queryParams: validQueryParams,
      config,
      listingTypePathParam,
    });
  }

  saveScrollStateBeforeListingNavigation() {
    if (typeof window === 'undefined') {
      return;
    }

    const validQueryParams = validUrlQueryParamsFromProps(this.props);
    const queryKey = this.getSearchQueryKey(validQueryParams);
    saveSearchScrollState({
      location: this.props.location,
      scrollY: window.scrollY || 0,
      loadedItemsCount: this.props.listings?.length || 0,
      currentPage: this.props.currentPage || 1,
      queryKey,
    });
  }

  scheduleScrollRestore() {
    if (typeof window === 'undefined' || !this.pendingScrollRestore || this.restoreScrollRaf) {
      return;
    }

    this.restoreScrollRaf = window.requestAnimationFrame(() => {
      this.restoreScrollRaf = null;
      this.tryRestoreSavedScrollPosition();
    });
  }

  tryRestoreSavedScrollPosition() {
    if (typeof window === 'undefined' || !this.pendingScrollRestore) {
      return;
    }

    const currentRouteKey = getSearchRouteKey(this.props.location);
    if (currentRouteKey !== this.pendingScrollRestore.routeKey) {
      this.pendingScrollRestore = null;
      this.restoreScrollAttempts = 0;
      return;
    }

    const loadedItemsCount = this.props.listings?.length || 0;
    const targetItemsCount = Number(this.pendingScrollRestore.loadedItemsCount || 0);
    const hasEnoughItemsRendered = loadedItemsCount >= targetItemsCount;
    const shouldRestoreNow =
      (!this.props.searchInProgress && hasEnoughItemsRendered) ||
      this.restoreScrollAttempts >= RESTORE_SCROLL_MAX_TRIES;

    if (shouldRestoreNow) {
      window.scrollTo(0, Number(this.pendingScrollRestore.scrollY || 0));
      clearSearchScrollState(this.props.location);
      this.pendingScrollRestore = null;
      this.restoreScrollAttempts = 0;
      return;
    }

    this.restoreScrollAttempts += 1;
    this.scheduleScrollRestore();
  }

  handleLoadMore({ hasNextPage, isFetchingNextPage, searchInProgress, currentPage, queryKey }) {
    const {
      onLoadMoreListings,
      location,
      config,
      history,
      routeConfiguration,
      params: currentPathParams = {},
    } = this.props;

    const guardReason = !hasNextPage
      ? 'no-next-page'
      : isFetchingNextPage
      ? 'fetch-in-progress-prop'
      : searchInProgress
      ? 'search-in-progress'
      : null;
    if (guardReason) {
      debugInfiniteScroll('load-more-skipped', {
        context: 'SearchPageWithMap',
        reason: guardReason,
        currentPage: Number(currentPage || 1),
        nextPage: Number(currentPage || 1) + 1,
        hasMore: hasNextPage,
        isFetchingNextPage,
        searchInProgress,
        queryKey: queryKey || null,
        loadedCount: this.props.listings?.length || 0,
        totalItems: Number(this.props.pagination?.totalItems || 0),
      });
      return;
    }
    const nextPage = Number(currentPage || 1) + 1;
    debugInfiniteScroll('load-more-request', {
      context: 'SearchPageWithMap',
      currentPage: Number(currentPage || 1),
      nextPage,
      queryKey: queryKey || null,
      hasMore: hasNextPage,
      isFetchingNextPage,
      searchInProgress,
      loadedCount: this.props.listings?.length || 0,
      totalItems: Number(this.props.pagination?.totalItems || 0),
      search: location.search,
    });

    return onLoadMoreListings({
      search: location.search,
      config,
      params: currentPathParams,
      page: nextPage,
      queryKey,
    })
      .then(response => {
        if (!response) {
          return response;
        }

        const responseListings = Array.isArray(response?.data?.data)
          ? response.data.data.length
          : 0;
        const loadedPage = Number(response?.data?.meta?.page || nextPage);
        debugInfiniteScroll('load-more-success', {
          context: 'SearchPageWithMap',
          requestedPage: loadedPage,
          queryKey: queryKey || null,
          responseCount: responseListings,
          totalPages: Number(response?.data?.meta?.totalPages || 0),
          totalItems: Number(response?.data?.meta?.totalItems || 0),
        });

        const currentQueryKey = this.getSearchQueryKey(validUrlQueryParamsFromProps(this.props));
        if (currentQueryKey !== queryKey) {
          debugInfiniteScroll('load-more-skipped', {
            context: 'SearchPageWithMap',
            reason: 'query-key-changed-before-url-update',
            requestedPage: loadedPage,
            queryKey: queryKey || null,
            currentQueryKey: currentQueryKey || null,
          });
          return response;
        }

        const queryParams = parse(this.props.location.search, {
          latlng: ['origin'],
          latlngBounds: ['bounds'],
        });

        const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
          routeConfiguration,
          this.props.location
        );

        history.replace(
          createResourceLocatorString(routeName, routeConfiguration, pathParams, {
            ...queryParams,
            page: loadedPage,
          })
        );
        return response;
      })
      .catch(error => {
        debugInfiniteScroll('load-more-failed', {
          context: 'SearchPageWithMap',
          requestedPage: nextPage,
          queryKey: queryKey || null,
          status: error?.status || null,
          code: error?.data?.errors?.[0]?.code || null,
          message: error?.message || null,
        });
        throw error;
      })
      .finally(() => {
        debugInfiniteScroll('load-more-settled', {
          context: 'SearchPageWithMap',
          requestedPage: nextPage,
          queryKey: queryKey || null,
        });
      });
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const { params: currentPathParams = {} } = this.props;

    const routes = this.props.routeConfiguration;
    const searchPagePath = currentPathParams.listingType
      ? pathByRouteName('SearchPageWithListingType', routes, currentPathParams)
      : pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { listingType: listingTypePathParam } = currentPathParams;
      const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
      const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      // parse query parameters, including a custom attribute named category
      // when onMapMoveEnd is called, pagination needs to be reset.
      const { address, bounds, mapSearch, page, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routes, location);

      history.push(createResourceLocatorString(routeName, routes, pathParams, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const {
      history,
      routeConfiguration,
      config,
      params: currentPathParams = {},
      location,
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  handleKeywordSearch(keywords) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = {
      ...urlQueryParams,
      ...this.state.currentQueryParams,
      keywords: keywords || null,
      page: null,
    };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    this.setState({ currentQueryParams: searchParams });
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
  }

  clearKeywordsOnly() {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const mergedQueryParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const currentKeywords = `${mergedQueryParams.keywords || ''}`.trim();
    if (!currentKeywords) {
      return;
    }

    const nextQueryParams = {
      ...mergedQueryParams,
      keywords: null,
      page: null,
    };
    const search = cleanSearchFromConflictingParams(nextQueryParams, filterConfigs, sortConfig);
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    this.setState({ currentQueryParams: nextQueryParams });
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      currentPage = 0,
      hasNextPage = false,
      isFetchingNextPage = false,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { sortConfig } = config?.search || {};

    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;

    const isMobileLayout = this.state.isMobileLayout;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
    ];
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = getActiveFiltersFromQuery(searchParamsInURL, filterConfigs);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isSearchStateDirty = isSearchDirty({
      queryParams: validQueryParams,
      filterConfigs,
      sortConfig,
      isKeywordSearch,
    });

    const selectedSecondaryFiltersCount = getSelectedSecondaryFiltersCount(
      validQueryParams,
      filterConfigs,
      customSecondaryFilters
    );

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const { totalItems, hasServerTotalItems } = getTotalItemsFromPagination({
      pagination,
      searchParamsAreInSync,
      loadedListingsCount: listings.length,
      context: 'SearchPageWithMap',
    });
    const listingsAreLoaded = !searchInProgress && searchParamsAreInSync && hasServerTotalItems;
    const searchQueryKey = this.getSearchQueryKey(validQueryParams);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Parse page heading to be included in the title
    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema, heading } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        className={css.pageRoot}
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div id="main-content" className={css.container} role="main">
          <div className={css.searchResultContainer}>
            <SearchFiltersMobile
              className={css.searchFiltersMobileMap}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={this.onOpenMobileModal}
              onCloseModal={this.onCloseMobileModal}
              resetAll={this.resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              noResultsInfo={noResultsInfo}
              location={location}
              isMapVariant
            >
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;
                return (
                  <FilterComponent
                    key={key}
                    id={filterId}
                    config={filterConfig}
                    containerId="SearchPage_MobileFilters"
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}
            </SearchFiltersMobile>
            <MainPanelHeader
              heading={heading}
              className={css.mainPanelMapVariant}
              sortByComponent={sortBy('desktop')}
              isSortByActive={sortConfig.active}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
              onClearAll={this.resetAll}
              onClearKeywords={this.clearKeywordsOnly}
              onSubmitSearch={this.handleKeywordSearch}
              keywords={validQueryParams.keywords}
              isSearchDirty={isSearchStateDirty}
            >
              <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                {availablePrimaryFilters.map(filterConfig => {
                  const key = `SearchFiltersPrimary.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersPrimary.${filterConfig.key.toLowerCase()}`;
                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPageWithMap_PrimaryFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
              </SearchFiltersPrimary>
            </MainPanelHeader>
            {isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(filterConfig => {
                    const key = `SearchFiltersSecondary.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    const filterId = `SearchFiltersSecondary.${filterConfig.key.toLowerCase()}`;
                    return (
                      <FilterComponent
                        key={key}
                        id={filterId}
                        config={filterConfig}
                        containerId="SearchPageWithMap_SecondaryFilters"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  setActiveListing={onActivateListing}
                  isMapVariant
                  onListingClick={this.saveScrollStateBeforeListingNavigation}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  searchInProgress={searchInProgress}
                  queryKey={searchQueryKey}
                  onLoadMore={() =>
                    this.handleLoadMore({
                      hasNextPage,
                      isFetchingNextPage,
                      searchInProgress,
                      currentPage,
                      queryKey: searchQueryKey,
                    })
                  }
                />
              </div>
            )}
          </div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage_map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage_map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>
        <GlobalBottomNavMobile />
      </Page>
    );
  }
}

/**
 * SearchPage component with map layout
 *
 * @param {Object} props
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onActivateListing] - The function to activate a listing
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPage,
    hasNextPage,
    isFetchingNextPage,
    activeListingId,
  } = state.SearchPage;
  const listings = filterPublicListings(getListingsById(state, currentPageResultIds));

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPage,
    hasNextPage,
    isFetchingNextPage,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onLoadMoreListings: params => dispatch(loadMoreListings(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
